
import {
  Component,
  computed,
  Inject,
  input,
  output,
} from '@angular/core';
import {
  toObservable,
  toSignal,
} from '@angular/core/rxjs-interop';
import {
  combineLatest,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import {
  CRMApiService,
  CrmObject,
} from '@vendasta/crm';
import {
  IAMService,
  User,
  UserIdentifier,
} from '@vendasta/iamv2';
import { MEETING_ANALYSIS_NAMESPACE_INJECTION_TOKEN$ } from '@galaxy/meeting-analysis/static';
import { map } from 'rxjs/operators';
import { RouterModule } from '@angular/router';
import { ParticipantComponent } from '../participants/participant/participant.component';
import { AttendeeComponent } from '../attendees/attendee/attendee.component';
import { MatMenuModule } from '@angular/material/menu';
import { AvatarGroupComponent } from '@vendasta/galaxy/avatar';
import {
  getFormattedContactDisplayName,
  getFormattedDisplayName,
} from '../../users.utility';
import { CrmFieldService } from '@galaxy/crm/services/field-service';

interface DisplayItem {
  type: 'contact' | 'user';
  contact?: CrmObject;
  user?: User;
  id: string;
}

@Component({
  selector: 'meeting-analysis-guests',
  standalone: true,
  imports: [RouterModule, ParticipantComponent, AttendeeComponent, MatMenuModule, AvatarGroupComponent],
  template: `
    <div class="combined">
      @if (useAvatarGroup()) {
        <glxy-avatar-group class="avatar-group" [avatarList]="avatarList()" [avatarWidth]="28"></glxy-avatar-group>
      } @else {
        @for (item of displayItems(); track item.id) {
          <div class="attendee-item">
            @if (item.type === 'contact') {
              <meeting-analysis-participant
                [contact]="item.contact!"
                [linkToContactPage]="false"
                [shape]="'square'"
              ></meeting-analysis-participant>
            } @else {
              <meeting-analysis-attendee
                [user]="item.user!"
                [shape]="'square'"
                [showProfilePicture]="false"
              ></meeting-analysis-attendee>
            }
          </div>
        }
        @if (morePeopleText()) {
          <div class="count" (click)="morePeopleClicked()">{{ morePeopleText() }}</div>
        }
      }
    </div>
  `,
  styleUrls: ['./guests.component.scss'],
})
export class GuestsComponent {
  useAvatarGroup = input<boolean>(false);
  contactIds = input<string[]>([]);
  userIds = input<string[]>([]);
  readonly jumpToGuestsTab = output<boolean>();

  // Computed properties to ensure unique IDs
  uniqueContactIds = computed(() => {
    const ids = this.contactIds();
    return [...new Set(ids)];
  });

  uniqueUserIds = computed(() => {
    const ids = this.userIds();
    return [...new Set(ids)];
  });

  contacts$ = combineLatest([this.namespace$, toObservable(this.uniqueContactIds)]).pipe(
    switchMap(([namespace, contactIds]) => {
      if (contactIds.length === 0) {
        return of({ crmObjects: [] });
      }
      return this.contactService.getMultiContact({
        namespace: namespace,
        crmObjectIds: contactIds,
      });
    }),
    map((response) => response?.crmObjects || []),
    map((contacts) => contacts.filter((contact) => contact !== null)),
  );
  contactItems = toSignal(
    this.contacts$.pipe(
      map((contacts) =>
        contacts.map(
          (contact) =>
            ({
              type: 'contact',
              contact: contact,
              id: contact.crmObjectId,
            }) as DisplayItem,
        ),
      ),
    ),
  );

  users$ = toObservable(this.uniqueUserIds).pipe(
    switchMap((userIds) => {
      if (userIds.length === 0) {
        return of([]);
      }
      const userIdentifiers = userIds.map((userId) => ({ userId }) as UserIdentifier);
      return this.iamService.getMultiUsers(userIdentifiers);
    }),
    map((users) => users.filter((user) => user !== null)),
  );
  userItems = toSignal(
    this.users$.pipe(
      map((users) =>
        users.map(
          (u) =>
            ({
              type: 'user',
              user: u,
              id: u.userId,
            }) as DisplayItem,
        ),
      ),
    ),
  );

  displayItems = computed(() => {
    const allItems = [...(this.contactItems() || []), ...(this.userItems() || [])];
    return allItems.slice(0, 3);
  });

  avatarList = computed(() => {
    const allItems = [...(this.contactItems() || []), ...(this.userItems() || [])];
    return allItems.map((item) => {
      if (item.type === 'contact') {
        return {
          name: getFormattedContactDisplayName(item.contact!, this.fieldsService),
          src: '',
        };
      } else {
        return {
          name: getFormattedDisplayName(item.user),
          src: item.user?.profileImageUrl || '',
        };
      }
    });
  });

  morePeopleText = computed(() => {
    const total = (this.contactItems()?.length || 0) + (this.userItems()?.length || 0);
    const moreCount = total - 3;
    if (moreCount > 0) {
      return `+${moreCount}`;
    }
    return '';
  });

  constructor(
    private readonly contactService: CRMApiService,
    private readonly iamService: IAMService,
    private readonly fieldsService: CrmFieldService,
    @Inject(MEETING_ANALYSIS_NAMESPACE_INJECTION_TOKEN$) private readonly namespace$: Observable<string>,
  ) {}

  morePeopleClicked(): void {
    this.jumpToGuestsTab.emit(true);
  }
}
